import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// components
import ChromePicker from "react-color/lib/Chrome";

// utilities
import { isValidHex } from "@utils/input-helpers";
import { gaTag } from "@utils/browser-helpers";
import { alternatingGradient } from "@styles/synchrony-core-palette.js";

const style = {
  defaultColorPickerStyle: {
    mainContainer: "relative",
    subContainer: "top-[35px] right-[34px]",
  },
};

const ColorSwabSelector = ({
  color,
  colorPickerEnabled,
  colorPickerCallback,
  onColorCloseCallback = () => null,
  colorSwabStyle = "",
  colorPickerStyle = { mainContainer: "", subContainer: "" },
  flexibleHexCodes = false,
  colorPickerId,
  returnErrorStatus,
}) => {
  const [pickerOpen, setPickerState] = useState(false);
  const [pickerColor, setPickerColor] = useState(color);

  useEffect(() => {
    if (!pickerColor !== color) {
      const isValid = isValidHex(color, flexibleHexCodes);
      if (returnErrorStatus) {
        returnErrorStatus(color ? !isValid : false);
      }
      setPickerColor(isValid ? color : null);
    }
    return () => setPickerColor(null);
  }, [color]);

  const { mainContainer, subContainer } = style.defaultColorPickerStyle;
  return (
    <div className={`${mainContainer} ${colorPickerStyle.mainContainer} `}>
      <div
        id={`color-swab__${colorPickerId}`}
        data-ga_click_id={`colorSwab: ${colorPickerId}`}
        data-ga_tag={gaTag()}
        className={`w-[34px] text-white flex h-[34px] rounded-none border border-solid ${
          pickerColor === "#ffffff" ? "border-color-gray-29" : "border-disabled"
        } box-border ${colorSwabStyle}`}
        style={{ background: pickerColor || alternatingGradient }}
        onClick={() =>
          colorPickerEnabled ? setPickerState(!pickerOpen) : null
        }
      />
      {pickerOpen && colorPickerEnabled ? (
        <div
          className={`absolute z-[2] ${subContainer} ${colorPickerStyle.subContainer}`}
          onMouseLeave={() => {
            setPickerState(false);
            onColorCloseCallback(color);
          }}
        >
          <ChromePicker
            color={color}
            onChangeComplete={({ hex }) => {
              colorPickerCallback ? colorPickerCallback(hex) : null;
              setPickerColor(isValidHex(hex, flexibleHexCodes) ? hex : null);
            }}
            disableAlpha
          />
        </div>
      ) : null}
    </div>
  );
};

ColorSwabSelector.propTypes = {
  /** id for color picker id target given `color-swab__${colorPickerId} $*/
  colorPickerId: PropTypes.string,
  /** control value for text input color */
  color: PropTypes.string,
  /** boolean to enable colorPickerCallback and native color picker selection; action on click vs view mode */
  colorPickerEnabled: PropTypes.bool,
  /** sets additional action on color selection */
  colorPickerCallback: PropTypes.func,
  /** optional style override for color swab */
  colorSwabStyle: PropTypes.string,
  /** optional style override for color picker components; format is {mainContainer: {}, subContainer: {}}  */
  colorPickerStyle: PropTypes.object,
  /** boolean to determine if hexcode validation for short codes enabled; iif false will only accept 6 char hex codes */
  flexibleHexCodes: PropTypes.bool,
  /** function that retrieves and handles result of hex validation */
  returnErrorStatus: PropTypes.func,
};

export default ColorSwabSelector;
